import React, { Fragment, useMemo } from 'react';
import type { ActionId, ActionImpl } from 'kbar';
import { Avatar } from 'antd';
import { css } from '@emotion/react';
import styled from 'types/theme.types';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import { useTheme } from 'hooks/use-theme';

export const KbarResultItem = React.forwardRef(function ResultItem(
  {
    action,
    active,
    currentRootActionId
  }: {
    action: ActionImpl;
    active: boolean;
    currentRootActionId: ActionId;
  },
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const { isDarkTheme } = useTheme();

  const ancestors = useMemo(() => {
    if (!currentRootActionId) return action.ancestors;
    const index = action.ancestors.findIndex(ancestor => ancestor.id === currentRootActionId);

    return action.ancestors.slice(index + 1);
  }, [action.ancestors, currentRootActionId]);
  return (
    <ResultItemElement ref={ref} active={active}>
      <ItemContent>
        {ancestors.map(ancestor => (
          <Fragment key={ancestor.id}>
            <AncestorNameElement>{ancestor.name}</AncestorNameElement>
            <ArrowElement>&rsaquo;</ArrowElement>
          </Fragment>
        ))}
        {/* Element icons are the new design's antd line glyphs (matching the sidebar) and render
            directly on the icon token; string icons are the legacy SVG URLs, kept on the Avatar with
            its dark-mode invert. Search results (projects, environments) still pass URLs in both
            designs. */}
        {action.icon &&
          (React.isValidElement(action.icon) ? (
            <IconGlyph>{action.icon}</IconGlyph>
          ) : (
            <StyledAvatar src={action.icon} shape="square" isDarkTheme={isDarkTheme} size={20} />
          ))}
        <span data-e2e={`kbar-action-${action.id}`}>{action.name}</span>
      </ItemContent>
    </ResultItemElement>
  );
});

const IconGlyph = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 16px;
  color: var(--icon-primary-standalone);
`;

type ActiveProps = { active?: boolean };

const ResultItemElement = styled.div<ActiveProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;

  ${({ active }) =>
    legacyOnly(
      theme => css`
        padding: 12px 16px;
        border: 4px solid ${theme.offWhite};
        border-radius: 8px;
        color: ${theme.primaryBlack};
        background-color: ${active ? theme.plainGray : 'inherit'};
      `
    )}

  ${({ active }) =>
    newDesignOnly(
      theme => css`
        /* The 4px border was faking a gap between rows; a margin says it. */
        margin: ${theme.spacing.xxxs} ${theme.spacing.xs};
        padding: ${theme.padding.xs} ${theme.padding.md};
        border-radius: ${theme.radius['200']};
        color: var(--text-heading);
        background-color: ${active ? 'var(--surface-main-element-on-card)' : 'inherit'};
      `
    )}
`;

const ItemContent = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  align-items: center;
  font-size: 14px;
`;

const AncestorNameElement = styled.span`
  margin-right: ${({ theme }) => theme.spacing.xs};

  ${legacyOnly(css`
    opacity: 0.5;
  `)}

  ${newDesignOnly(css`
    color: var(--text-body);
  `)}
`;

const StyledAvatar = styled(Avatar, {
  shouldForwardProp: (propName: string) => propName !== 'isDarkTheme'
})<{ isDarkTheme?: boolean }>`
  filter: ${({ isDarkTheme }) => (isDarkTheme ? 'invert(1)' : 'none')};
`;

const ArrowElement = styled.span`
  margin-right: ${({ theme }) => theme.spacing.xs};
`;
